import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  Star, CalendarCheck, Factory, MapPin, ArrowRight, ArrowUpRight,
  Package, MapPinned, MessagesSquare, Cog, Phone, MessageCircle, FileText, Sparkles,
} from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import SafeImage from "@/components/SafeImage";
import ProductCard from "@/components/ProductCard";
import { Reveal, MaskedLines, Kicker } from "@/components/Reveal";
import { api, telLink, waLink } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const HERO_IMG = "/api/files/hariom-trading/site/hero-products-banner.jpg";
const PANEL_IMG = "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1000&q=70";

function Hero({ settings }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-[#111111]" data-testid="hero-section">
      <motion.div aria-hidden="true" style={{ y: yImg }} className="absolute inset-0">
        <SafeImage
          src={HERO_IMG}
          alt="Industrial electrical and automation products — contactor, MCCB, motor protection breaker, temperature controller, panel fan and solenoid valve"
          className="h-full w-full object-cover object-right"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/85 to-[#111111]/10" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-[#111111]/50" aria-hidden="true" />
        <div className="absolute inset-0 bg-[#111111]/45 lg:bg-transparent" aria-hidden="true" />
      </motion.div>
      <motion.div
        aria-hidden="true"
        style={{ y: yGlow }}
        className="absolute -right-32 top-10 h-[480px] w-[480px] rounded-full bg-[#F5B700] opacity-[0.07] blur-[110px]"
      />
      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 lg:pb-32 lg:pt-28">
        <div className="max-w-2xl">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Kicker dark>Electrical Equipment Supplier · Est. {settings.established}</Kicker>
          </motion.div>

          <h1 className="mt-6 font-heading text-[38px] font-bold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-[64px]" data-testid="hero-headline">
            <MaskedLines
              lines={[
                <>Industrial Electrical,</>,
                <>Automation &amp; <span className="text-[#F5B700]">Pneumatic</span></>,
                <>Products — <span className="text-stroke-yellow">Tahliwal</span></>,
              ]}
            />
          </h1>

          <motion.p
            className="mt-7 max-w-xl text-base leading-relaxed text-[#b8bec8] sm:text-lg"
            data-testid="hero-subcopy"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
          >
            Hari Om Trading Co. supplies electrical, industrial automation, switchgear, lighting,
            pneumatic and related products for businesses, workshops, contractors and industrial applications.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-wrap gap-3"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
          >
            <Link
              to="/products"
              data-testid="hero-browse-products"
              className="group flex min-h-[52px] items-center gap-2 bg-[#F5B700] px-7 py-3.5 font-heading text-[15px] font-semibold text-[#111111] transition-colors hover:bg-[#D99C00]"
            >
              Browse Products
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <Link
              to="/quote"
              data-testid="hero-request-quote"
              className="flex min-h-[52px] items-center gap-2 border border-[#3a3d42] px-7 py-3.5 font-heading text-[15px] font-semibold text-white transition-colors hover:border-[#F5B700] hover:text-[#F5B700]"
            >
              Request a Quote
            </Link>
          </motion.div>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 inline-flex items-center gap-4 border border-[#F5B700]/40 bg-[#111111]/70 px-6 py-4 backdrop-blur-sm"
            data-testid="hero-rating-chip"
          >
            <p className="font-heading text-3xl font-bold text-[#F5B700]">{settings.google_rating}★</p>
            <p className="text-xs font-medium uppercase tracking-widest text-[#b8bec8]">
              Google Rating · {settings.google_reviews_count} reviews
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip({ settings }) {
  const items = [
    { icon: Star, title: `${settings.google_rating}★ Google Rating`, sub: `${settings.google_reviews_count} verified reviews` },
    { icon: CalendarCheck, title: `Established ${settings.established}`, sub: "Serving local industry" },
    { icon: Factory, title: "Industrial Product Supplier", sub: "Automation · Switchgear · Lighting" },
    { icon: MapPin, title: "Tahliwal, Una", sub: "Himachal Pradesh 174507" },
  ];
  return (
    <section className="border-b border-[#D9DDE3] bg-white" data-testid="trust-strip">
      <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
        {items.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.08} className={`flex items-start gap-3.5 border-[#D9DDE3] px-5 py-6 sm:px-7 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${i < 3 ? "lg:border-r" : ""}`}>
            <item.icon size={22} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
            <div>
              <p className="font-heading text-[15px] font-semibold text-[#161616]">{item.title}</p>
              <p className="mt-0.5 text-xs text-[#667085]">{item.sub}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Categories({ meta }) {
  if (!meta) return null;
  return (
    <section className="bg-[#F6F7F8] py-20 lg:py-28" data-testid="categories-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <Kicker>Catalogue</Kicker>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-xl font-heading text-4xl font-bold tracking-tight text-[#161616] lg:text-5xl">
              Explore Our Product Range
            </h2>
            <Link to="/products" data-testid="categories-view-all" className="group flex items-center gap-2 font-heading text-[15px] font-semibold text-[#161616] hover:text-[#8a6800]">
              View full catalogue <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {meta.categories.map((cat, i) => {
            const count = meta.category_counts?.[cat.slug] || 0;
            return (
              <Reveal key={cat.slug} delay={i * 0.06}>
                <Link
                  to={`/products?category=${cat.slug}`}
                  data-testid={`category-card-${cat.slug}`}
                  className="group relative block overflow-hidden bg-[#202124]"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <SafeImage
                      src={cat.image}
                      alt={`${cat.name} category`}
                      className="h-full w-full object-cover opacity-90 transition-all duration-500 group-hover:scale-[1.06] group-hover:opacity-100"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/30 to-transparent" aria-hidden="true" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#F5B700]">
                      {String(i + 1).padStart(2, "0")}{count > 0 ? ` · ${count} products` : ""}
                    </p>
                    <h3 className="mt-1.5 font-heading text-2xl font-semibold text-white">{cat.name}</h3>
                    <p className="mt-1.5 line-clamp-2 text-sm text-[#b8bec8]">{cat.desc}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F5B700]">
                      View Products
                      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MultispanSection() {
  const rows = [
    ["Temperature Controllers", "Temperature"],
    ["PID Controllers", "PID"],
    ["Programmable Timers", "Timer"],
    ["Programmable Counters", "Counter"],
    ["Protection Relays", "Protection Relay"],
    ["Power & Energy Meters", "Meter"],
  ];
  return (
    <section className="grain relative overflow-hidden bg-[#202124] py-20 lg:py-28" data-testid="multispan-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <Reveal>
          <Kicker dark>Featured Brand</Kicker>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-white lg:text-5xl">
            Multispan Automation &amp; Control Products
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[#b8bec8]">
            Panel automation essentials for industrial control — enquire with your required model
            number for availability and quotation.
          </p>
          <Link
            to="/products?brand=Multispan"
            data-testid="multispan-view-all"
            className="mt-8 inline-flex items-center gap-2 bg-[#F5B700] px-6 py-3.5 font-heading text-[15px] font-semibold text-[#111111] transition-colors hover:bg-[#D99C00]"
          >
            View Multispan Products <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </Reveal>
        <div>
          {rows.map(([label, type], i) => (
            <Reveal key={type} delay={i * 0.05}>
              <Link
                to={`/products?q=${encodeURIComponent(type)}`}
                data-testid={`multispan-type-${type.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex items-center justify-between border-b border-[#3a3d42] py-4 transition-colors hover:border-[#F5B700]"
              >
                <span className="flex items-baseline gap-4">
                  <span className="font-heading text-xs font-semibold text-[#F5B700]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-heading text-xl font-semibold text-white transition-transform duration-300 group-hover:translate-x-1.5 sm:text-2xl">
                    {label}
                  </span>
                </span>
                <ArrowUpRight size={20} className="text-[#667085] transition-colors group-hover:text-[#F5B700]" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  const cards = [
    { icon: Package, title: "Product Range", text: "Electrical, automation, switchgear, lighting, pneumatic and industrial tool categories under one roof." },
    { icon: MapPinned, title: "Local Accessibility", text: "Based on Garhshankar–Nangal Road, Tahliwal — easy to reach for businesses and workshops in the area." },
    { icon: MessagesSquare, title: "Product Enquiries", text: "Send a product name, model number or requirement by form, phone or WhatsApp for availability information." },
    { icon: Cog, title: "Industrial Focus", text: "A catalogue built around control panels, machines, workshops and industrial maintenance needs." },
  ];
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="why-choose-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <Kicker>Why Hari Om Trading Co.</Kicker>
          <h2 className="mt-4 max-w-2xl font-heading text-4xl font-bold tracking-tight text-[#161616] lg:text-5xl">
            Practical Product Support for Electrical &amp; Industrial Requirements
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="group h-full border border-[#D9DDE3] bg-[#F6F7F8] p-7 transition-colors duration-300 hover:border-[#111111]" data-testid={`why-card-${i}`}>
                <p className="font-heading text-sm font-bold text-[#F5B700]">{String(i + 1).padStart(2, "0")}</p>
                <c.icon size={30} className="mt-5 text-[#111111]" aria-hidden="true" />
                <h3 className="mt-4 font-heading text-xl font-semibold text-[#161616]">{c.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#667085]">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    ["Find a Product", "Browse the catalogue by category, brand or model number."],
    ["Send Your Requirement", "Share the product name, model and quantity by form, call or WhatsApp."],
    ["Get Product Assistance", "Receive availability and quotation information for your requirement."],
  ];
  return (
    <section className="bg-grid-light bg-[#F6F7F8] py-20 lg:py-28" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <Kicker>How it works</Kicker>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-[#161616] lg:text-5xl">
            Three Steps to Your Product
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {steps.map(([title, text], i) => (
            <Reveal key={title} delay={i * 0.1}>
              <div className="relative border border-[#D9DDE3] bg-white p-8" data-testid={`process-step-${i + 1}`}>
                <span className="font-heading text-6xl font-bold text-[#F5B700]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-5 font-heading text-xl font-semibold text-[#161616]">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#667085]">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  const s = useSettings();
  return (
    <section className="grain relative overflow-hidden bg-[#F5B700] py-20 lg:py-24" data-testid="final-cta">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <h2 className="max-w-3xl font-heading text-4xl font-bold tracking-tight text-[#111111] lg:text-5xl">
            Looking for a Specific Electrical or Industrial Product?
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#3d3300]">
            Send us the product name, model number or your requirement and contact Hari Om Trading Co.
            for availability and quotation information.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/quote" data-testid="cta-request-quote" className="flex min-h-[52px] items-center gap-2 bg-[#111111] px-7 py-3.5 font-heading text-[15px] font-semibold text-white transition-colors hover:bg-[#202124]">
              <FileText size={17} aria-hidden="true" /> Request a Quote
            </Link>
            <a href={telLink(s)} data-testid="cta-call-now" className="flex min-h-[52px] items-center gap-2 border-2 border-[#111111] px-7 py-3.5 font-heading text-[15px] font-semibold text-[#111111] transition-colors hover:bg-[#111111] hover:text-white">
              <Phone size={17} aria-hidden="true" /> Call Now
            </a>
            <a href={waLink(s)} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp" className="flex min-h-[52px] items-center gap-2 border-2 border-[#111111] px-7 py-3.5 font-heading text-[15px] font-semibold text-[#111111] transition-colors hover:bg-[#18864B] hover:border-[#18864B] hover:text-white">
              <MessageCircle size={17} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FestiveBanner({ settings }) {
  if (settings.festive_banner_enabled !== "true" || !(settings.festive_banner_text || "").trim()) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="relative overflow-hidden border-b-2 border-[#F5B700] bg-[#111111]"
      data-testid="festive-banner"
      role="region"
      aria-label="Seasonal offer"
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 w-40 bg-gradient-to-r from-transparent via-[#F5B700]/25 to-transparent"
        animate={{ x: ["-10rem", "110vw"] }}
        transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
      />
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 py-2.5 sm:px-6">
        <Sparkles size={15} className="shrink-0 text-[#F5B700]" aria-hidden="true" />
        <p className="text-center text-sm font-semibold text-white" data-testid="festive-banner-text">{settings.festive_banner_text}</p>
        {(settings.festive_banner_cta || "").trim() && (
          <Link
            to={(settings.festive_banner_link || "").trim() || "/quote"}
            data-testid="festive-banner-cta"
            className="group flex items-center gap-1 font-heading text-sm font-semibold text-[#F5B700] hover:text-white"
          >
            {settings.festive_banner_cta} <ArrowRight size={14} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
    </motion.div>
  );
}

export default function Home() {
  const settings = useSettings();
  const [meta, setMeta] = useState(null);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <>
      <SEO title={settings.seo_title} description={settings.seo_description} />
      <FestiveBanner settings={settings} />
      <Hero settings={settings} />
      <Marquee />
      <TrustStrip settings={settings} />
      <Categories meta={meta} />

      {featured.length > 0 && (
        <section className="bg-white py-20 lg:py-24" data-testid="featured-products">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <Reveal>
              <Kicker>From the catalogue</Kicker>
              <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
                <h2 className="font-heading text-4xl font-bold tracking-tight text-[#161616] lg:text-5xl">
                  Product Spotlight
                </h2>
                <Link to="/products" data-testid="featured-view-all" className="group flex items-center gap-2 font-heading text-[15px] font-semibold hover:text-[#8a6800]">
                  View all products <ArrowUpRight size={17} aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <MultispanSection />
      <WhyChooseUs />
      <Process />
      <FinalCTA />
    </>
  );
}
